import { useLayoutEffect, useRef, useState } from "react";
import type { SyntheticEvent } from "react";

import { useDetailLoadItem } from "../../../../../../hooks/use-embarcacao-detail-load-gate";
import { useEmbarcacaoImagemArquivo } from "../../../../../../hooks/use-embarcacao-imagem-arquivo";
import type { EmbarcacaoImagemTipo } from "@/modules/embarcacoes";
import {
  IMAGEM_CARD_DOWNLOAD_ERROR_MESSAGE,
  IMAGEM_CARD_DOWNLOAD_LABEL,
  IMAGEM_CARD_IMAGE_DOWNLOAD_LABEL,
  IMAGEM_CARD_PRINCIPAL_LABEL,
  IMAGEM_CARD_TEST_ID_PREFIX,
} from "./imagem-card.constants";
import { fileNameFromUrl, formatFileSize } from "./imagem-card.utils";
import type { ImagemCardProps } from "./types-imagem-card";

/**
 * Cartão da galeria: imagem, identificação, nome e informações do arquivo e o download (HU 1751). Clicar na imagem ou no
 * botão Download baixa o arquivo, sem abri-lo em outra janela (HU 2673); o download é do Model da seção.
 */
export function useImagemCard({ embarcacaoId, url, label, principal }: ImagemCardProps) {
  const imageRef = useRef<HTMLImageElement>(null);
  const [dimensoes, setDimensoes] = useState<string | null>(null);
  const [mediaReady, setMediaReady] = useState(false);
  const tipo: EmbarcacaoImagemTipo = principal ? "padrao" : "uhd";
  const testId = `${IMAGEM_CARD_TEST_ID_PREFIX}-${tipo}`;
  const fileName = fileNameFromUrl(url, label);
  const arquivo = useEmbarcacaoImagemArquivo({ id: embarcacaoId, tipo, url, fileName });

  // O Exportar do detalhe espera a imagem terminar de carregar (ou falhar).
  useDetailLoadItem(`${testId}-media`, !mediaReady);

  useLayoutEffect(() => {
    const image = imageRef.current;
    if (!image) return;

    setMediaReady(false);
    const settle = () => setMediaReady(true);

    if (image.complete) {
      settle();
      return;
    }

    image.addEventListener("load", settle);
    image.addEventListener("error", settle);
    return () => {
      image.removeEventListener("load", settle);
      image.removeEventListener("error", settle);
    };
  }, [url]);

  function aoCarregar(event: SyntheticEvent<HTMLImageElement>) {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (naturalWidth > 0) setDimensoes(`${naturalWidth} × ${naturalHeight}`);
  }

  return {
    testId,
    testIds: {
      image: `${testId}-image`,
      imageDownload: `${testId}-image-download`,
      fileName: `${testId}-file-name`,
      fileInfo: `${testId}-file-info`,
      download: `${testId}-download`,
      downloadError: `${testId}-download-error`,
    },
    url,
    label,
    principal,
    fileName,
    imageRef,
    aoCarregar,
    info: [dimensoes, arquivo.tamanhoBytes === null ? null : formatFileSize(arquivo.tamanhoBytes)]
      .filter(Boolean)
      .join(" · "),
    baixar: () => void arquivo.baixar(),
    downloadFalhou: arquivo.falhou,
    downloadLabel: IMAGEM_CARD_DOWNLOAD_LABEL,
    downloadAriaLabel: `${IMAGEM_CARD_DOWNLOAD_LABEL} ${label}`,
    imageDownloadAriaLabel: `${IMAGEM_CARD_IMAGE_DOWNLOAD_LABEL} ${label}`,
    principalLabel: IMAGEM_CARD_PRINCIPAL_LABEL,
    downloadErrorMessage: IMAGEM_CARD_DOWNLOAD_ERROR_MESSAGE,
  };
}
